import React from "react";
import {
  Button,
  Iconography,
  IconographySizes,
  Notification,
  NotificationVariants,
} from "@hid-galaxy-ui/galaxy-react";
import { Dialog, DialogContent, TextField } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../../../redux";
import {
  createLookupDataSagaAction,
  deleteLookupDataSagaAction,
  getLookupByCategorySagaAction,
  LookupCategoryType,
} from "../../../../../redux/saga/lookup-data/saga-actions";
import {
  selectLookupCategoryData,
  selectLookupStatus,
} from "../../../../../redux/slices/lookup-data";
import "./PartnerTypeVerticalSettings.scss";

const TOAST_AUTO_CLOSE_MS = 4500;

type AddModalState = {
  open: boolean;
  category: LookupCategoryType;
  value: string;
};

type DeleteModalState = {
  open: boolean;
  category: LookupCategoryType;
  item: { ID: number; value: string } | null;
};

const PartnerTypeVerticalSettings: React.FC<{ readOnly?: boolean }> = ({
  readOnly = false,
}) => {
  const dispatch = useAppDispatch();
  const partnerTypes = useAppSelector((state) =>
    selectLookupCategoryData(state, "partnerType")
  );
  const partnerVerticals = useAppSelector((state) =>
    selectLookupCategoryData(state, "partnerVertical")
  );
  const lookupStatus = useAppSelector(selectLookupStatus);
  const [toasts, setToasts] = React.useState<any[]>([]);
  const [addModal, setAddModal] = React.useState<AddModalState>({
    open: false,
    category: "partnerType",
    value: "",
  });
  const [deleteModal, setDeleteModal] = React.useState<DeleteModalState>({
    open: false,
    category: "partnerType",
    item: null,
  });

  React.useEffect(() => {
    dispatch(getLookupByCategorySagaAction({ category: "partnerType" }));
    dispatch(getLookupByCategorySagaAction({ category: "partnerVertical" }));
  }, [dispatch]);

  const pushSuccessToast = React.useCallback((message: string) => {
    setToasts((prev) => [
      ...prev,
      {
        id: Date.now(),
        title: "",
        message,
        variant: NotificationVariants.Success,
        icon: <Iconography icon="circleCheck" size={IconographySizes.Medium} />,
      },
    ]);
  }, []);

  const pushErrorToast = React.useCallback((message: string) => {
    setToasts((prev) => [
      ...prev,
      {
        id: Date.now(),
        title: "",
        message,
        variant: NotificationVariants.Error,
        icon: <Iconography icon="trashCan" size={IconographySizes.Medium} />,
      },
    ]);
  }, []);

  const openAddModal = (category: LookupCategoryType) => {
    setAddModal({
      open: true,
      category,
      value: "",
    });
  };

  const handleAddSubmit = () => {
    const value = addModal.value.trim();
    if (!value) {
      pushErrorToast("Please enter a value before saving.");
      return;
    }
    const categoryLabel =
      addModal.category === "partnerType" ? "Partner type" : "Partner vertical";
    dispatch(
      createLookupDataSagaAction({
        category: addModal.category,
        value,
        onSuccess: (message) => {
          pushSuccessToast(message || `${categoryLabel} added successfully.`);
          setAddModal((prev) => ({ ...prev, open: false, value: "" }));
          dispatch(getLookupByCategorySagaAction({ category: addModal.category }));
        },
        onError: (message) => {
          pushErrorToast(message || `Unable to add ${categoryLabel.toLowerCase()}.`);
        },
      })
    );
  };

  const addModalTitle =
    addModal.category === "partnerType" ? "Add Partner Type" : "Add Partner Vertical";
  const addModalSubText =
    addModal.category === "partnerType"
      ? "Configure a new partner type"
      : "Configure a new partner vertical";
  const addModalInputLabel =
    addModal.category === "partnerType" ? "Partner Type Name" : "Partner Vertical Name";
  const addModalPlaceholder =
    addModal.category === "partnerType" ? "e.g., Channel" : "e.g., Enterprise";
  const addModalPrimaryCta =
    addModal.category === "partnerType" ? "ADD PARTNER TYPE" : "ADD PARTNER VERTICAL";

  const openDeleteModal = (
    category: LookupCategoryType,
    item: { ID: number; value: string }
  ) => {
    setDeleteModal({
      open: true,
      category,
      item,
    });
  };

  const handleDeleteConfirm = () => {
    if (!deleteModal.item?.ID) {
      pushErrorToast("Unable to delete item. Missing ID.");
      return;
    }
    dispatch(
      deleteLookupDataSagaAction({
        ID: deleteModal.item.ID,
        onSuccess: (message) => {
          pushSuccessToast(message || "Item deleted successfully.");
          dispatch(getLookupByCategorySagaAction({ category: deleteModal.category }));
          setDeleteModal({ open: false, category: "partnerType", item: null });
        },
        onError: (message) => {
          pushErrorToast(message || "Unable to delete item.");
        },
      })
    );
  };

  const renderItems = (
    category: LookupCategoryType,
    items: { ID: number; value: string }[]
  ) => {
    if (!items.length) {
      return <div className="ptv-empty-list">No data available.</div>;
    }
    return (
      <div className="ptv-chip-grid">
        {items.map((item) => (
          <div key={`${category}-${item.ID}`} className="ptv-chip">
            <span className="ptv-chip-text">{item.value}</span>
            <button
              type="button"
              className="ptv-chip-delete"
              onClick={() => openDeleteModal(category, item)}
              aria-label={`Delete ${item.value}`}
              disabled={readOnly}
            >
              <Iconography icon="xmark" size={IconographySizes.Small} />
            </button>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="partner-type-vertical-settings">
      <div className="roles-permissions-notifications-host">
        <Notification
          toasts={toasts}
          setToasts={setToasts}
          autoClose={TOAST_AUTO_CLOSE_MS}
          isAutoClose={true}
        />
      </div>

      <section className="ptv-container">
        <header className="ptv-container-header">
          <h3>Partner Type &amp; Partner Vertical</h3>
          <p>Configure partner type &amp; vertical</p>
        </header>

        <div className="ptv-block">
          <header className="ptv-block-header">
            <h4>Partner Types</h4>
            <button
              type="button"
              className="ptv-add-btn"
              onClick={() => openAddModal("partnerType")}
              disabled={readOnly || lookupStatus.createLoading}
            >
              ADD PARTNER TYPE
              <Iconography icon="plus" size={IconographySizes.Small} />
            </button>
          </header>
          {lookupStatus.listLoading.partnerType ? (
            <div className="ptv-empty-list">Loading partner types...</div>
          ) : (
            renderItems("partnerType", partnerTypes)
          )}
        </div>

        <div className="ptv-block">
          <header className="ptv-block-header">
            <h4>Partner Verticals</h4>
            <button
              type="button"
              className="ptv-add-btn"
              onClick={() => openAddModal("partnerVertical")}
              disabled={readOnly || lookupStatus.createLoading}
            >
              ADD PARTNER VERTICAL
              <Iconography icon="plus" size={IconographySizes.Small} />
            </button>
          </header>
          {lookupStatus.listLoading.partnerVertical ? (
            <div className="ptv-empty-list">Loading partner verticals...</div>
          ) : (
            renderItems("partnerVertical", partnerVerticals)
          )}
        </div>
      </section>

      <Dialog
        open={addModal.open}
        onClose={() => setAddModal((prev) => ({ ...prev, open: false }))}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "6px",
            boxShadow: "0px 12px 32px rgba(15, 23, 42, 0.18)",
            maxWidth: 640,
          },
        }}
      >
        <div className="ptv-modal">
          <header className="ptv-modal-header">
            <div>
              <p className="ptv-modal-eyebrow">Partner Program Configuration</p>
              <h3>{addModalTitle}</h3>
              <p className="ptv-modal-subtitle">{addModalSubText}</p>
            </div>
            <button
              type="button"
              className="ptv-modal-close"
              onClick={() => setAddModal((prev) => ({ ...prev, open: false }))}
              aria-label="Close"
            >
              <Iconography icon="xmark" size={IconographySizes.Medium} />
            </button>
          </header>
          <DialogContent className="ptv-modal-content">
            <label className="ptv-modal-label">{addModalInputLabel}</label>
            <TextField
              value={addModal.value}
              onChange={(event) =>
                setAddModal((prev) => ({ ...prev, value: event.target.value }))
              }
              fullWidth
              placeholder={addModalPlaceholder}
              size="small"
            />
          </DialogContent>
          <footer className="ptv-modal-footer">
            <Button
              variant="secondary"
              label="CANCEL"
              onClick={() => setAddModal((prev) => ({ ...prev, open: false }))}
            />
            <Button
              variant="primary"
              label={addModalPrimaryCta}
              onClick={handleAddSubmit}
              disabled={readOnly || lookupStatus.createLoading}
            />
          </footer>
        </div>
      </Dialog>

      <Dialog
        open={deleteModal.open && Boolean(deleteModal.item)}
        onClose={() => setDeleteModal({ open: false, category: "partnerType", item: null })}
        maxWidth="sm"
        fullWidth
      >
        <div className="ptv-modal ptv-delete-modal">
          <header className="ptv-modal-header">
            <div className="ptv-delete-heading">
              <span className="ptv-delete-icon">
                <Iconography icon="circleExclamation" size={IconographySizes.Small} />
              </span>
              <h3>
                Delete{" "}
                {deleteModal.category === "partnerType"
                  ? "Partner Type"
                  : "Partner Vertical"}
                ?
              </h3>
            </div>
          </header>
          <DialogContent className="ptv-modal-content">
            <p>
              Are you sure to delete{" "}
              <strong>&quot;{deleteModal.item?.value ?? ""}&quot;</strong>. This action
              cannot be undone.
            </p>
            <div className="ptv-warning-box" role="alert">
              <Iconography icon="circleExclamation" size={IconographySizes.Medium} />
              <div>
                <h4>Warning notification</h4>
                <p>Partners currently assigned to tiers with this {" "}
                {deleteModal.category === "partnerType"
                  ? "Partner Type"
                  : "Partner Vertical"} will no longer have access to it.</p>
              </div>
            </div>
          </DialogContent>
          <footer className="ptv-modal-footer">
            <Button
              variant="secondary"
              label="CANCEL"
              onClick={() =>
                setDeleteModal({ open: false, category: "partnerType", item: null })
              }
            />
            <Button
              variant="primary"
              label="DELETE"
              onClick={handleDeleteConfirm}
              disabled={readOnly || lookupStatus.deleteLoading}
            />
          </footer>
        </div>
      </Dialog>
    </div>
  );
};

export default PartnerTypeVerticalSettings;
